import React, { Component } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { UsageKindChip, UsageStatusChip, UsageApiChip, UsageDurationBar, UsageChipFilter, kindOptions, fmtTime, fmtDateTime, fmtMs, fmtInt } from './usage-widgets';
import { t } from './usage-i18n';

// ---------------------------------------------------------------------------
// Uma VISITA à tela: cabeçalho com o resumo, expandir carrega os requests filhos (lazy).
// loadRows(screenVisitId) => Promise<rows>. onRowClick(row) abre o drawer.
// ---------------------------------------------------------------------------

export default class UsageVisitAccordion extends Component {
  constructor(props) {
    super(props);
    // defaultExpanded: chega pelo link 'ver a visita' do drawer — abre e carrega os filhos de cara.
    this.state = { expanded: !!props.defaultExpanded, loading: false, rows: null, kind: '' }; // kind: filtro por tipo dentro da visita (só na tela)
    this.toggle = this.toggle.bind(this);
  }

  componentDidMount() { if (this.state.expanded) this.loadIfNeeded(); }

  toggle() {
    const next = !this.state.expanded;
    this.setState({ expanded: next });
    if (next) this.loadIfNeeded();
  }

  loadIfNeeded() {
    if (this.state.rows === null && this.props.loadRows) {
      this.setState({ loading: true });
      this.props.loadRows(this.props.visit.screenVisitId)
        .then((rows) => this.setState({ rows: rows || [], loading: false }))
        .catch(() => this.setState({ rows: [], loading: false }));
    }
  }

  render() {
    const { intl, visit, onRowClick, onUserClick } = this.props;
    const volumeAlertRows = Number(this.props.volumeAlertRows) || 5000; // item 5: limiar por contratante (vem da página)
    const { expanded, loading, rows, kind } = this.state;
    const all = (rows || []).filter((r) => r.actionKind !== 'screen');
    // Tipos presentes nesta visita, com contagem: chips para ver só as exportações (ou só as recusas) sem sair do modo "Por visita".
    const kindCounts = all.reduce((acc, r) => { acc[r.actionKind] = (acc[r.actionKind] || 0) + 1; return acc; }, {});
    const kindOpts = kindOptions(intl, Object.keys(kindCounts)).map((o) => ({ ...o, count: kindCounts[o.id] }));
    const children = kind ? all.filter((r) => r.actionKind === kind) : all;
    const maxMs = Math.max(1000, ...children.map((r) => Number(r.durationMs) || 0));
    const span = visit.lastAt ? Math.max(0, new Date(visit.lastAt) - new Date(visit.occurredAt)) : 0;

    return (
      <Accordion expanded={expanded} onChange={this.toggle} style={{ border: '1px solid #e0e0e0', borderRadius: 12, marginBottom: 8, boxShadow: 'none' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 10, width: '100%' }}>
            <Typography variant="body2" style={{ fontVariantNumeric: 'tabular-nums', color: '#666', minWidth: 150 }}>{fmtDateTime(visit.occurredAt)}</Typography>
            <UsageKindChip intl={intl} kind="screen" label={t(intl, 'usage.visit.screen', 'tela')} />
            <Tooltip title={visit.url || ''}><Typography variant="body2" style={{ fontWeight: 600 }}>{visit.screen}</Typography></Tooltip>
            {/* Nome do usuário só vem na listagem POR TELA; na linha do tempo o usuário é o filtro. */}
            {visit.userName || visit.userId ? (
              <Chip size="small" label={visit.userName || `#${visit.userId}`} style={{ background: '#009688', color: '#fff', cursor: onUserClick ? 'pointer' : 'default' }}
                onClick={onUserClick ? (e) => { e.stopPropagation(); onUserClick(visit); } : undefined} />
            ) : null}
            <Box flex={1} />
            <Chip size="small" variant="outlined" label={`${fmtInt(visit.requests)} ${t(intl, 'usage.visit.req', 'req')}`} />
            <Chip size="small" variant="outlined" label={`${fmtMs(span)} ${t(intl, 'usage.visit.onScreen', 'na tela')}`} />
            <Chip size="small" variant="outlined" label={`${fmtMs(visit.sumDurationMs)} ${t(intl, 'usage.visit.apiTime', 'de API')}`} />
            {visit.writes > 0 && <Chip size="small" label={`${visit.writes} ${t(intl, 'usage.visit.writes', 'escrita(s)')}`} style={{ background: '#8BC34A', color: '#fff' }} />}
            {visit.exports > 0 && <Chip size="small" label={`${visit.exports} ${t(intl, 'usage.visit.exports', 'exportação(ões)')}`} style={{ background: '#FF9800', color: '#fff' }} />}
            {visit.downloads > 0 && <Chip size="small" label={`${visit.downloads} ${t(intl, 'usage.visit.downloads', 'anexo(s) aberto(s)')}`} style={{ background: '#00ACC1', color: '#fff' }} />}
            {visit.denials > 0 && <Chip size="small" label={`${visit.denials} ${t(intl, 'usage.visit.denials', 'recusa(s)')}`} style={{ background: '#F44336', color: '#fff' }} />}
            {visit.errors > 0 && <Chip size="small" label={`${visit.errors} ${t(intl, 'usage.visit.errors', 'erro(s)')}`} style={{ background: '#B71C1C', color: '#fff' }} />}
            <Typography variant="caption" style={{ color: '#999' }}>{visit.ip}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails style={{ padding: 0 }}>
          {loading && <Box display="flex" justifyContent="center" style={{ padding: 16 }}><CircularProgress size={22} color="secondary" /></Box>}
          {!loading && rows && all.length > 1 && Object.keys(kindCounts).length > 1 && (
            <Box style={{ padding: '8px 12px 0' }}>
              <UsageChipFilter label={t(intl, 'usage.chip.kind', 'Tipo')} allLabel={`${t(intl, 'usage.ua.action.all', 'Todas')} · ${fmtInt(all.length)}`} value={kind} onChange={(v) => this.setState({ kind: v })} options={kindOpts} />
            </Box>
          )}
          {!loading && rows && children.length === 0 && <Typography variant="body2" style={{ color: '#999', padding: 12 }}>{t(intl, 'usage.visit.none', 'Nenhum request registrado nesta visita.')}</Typography>}
          {!loading && children.length > 0 && (
            <Box style={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t(intl, 'usage.col.time', 'Hora')}</TableCell><TableCell>{t(intl, 'usage.col.action', 'Ação')}</TableCell><TableCell>API</TableCell><TableCell>{t(intl, 'usage.col.route', 'Rota')}</TableCell>
                    <TableCell>{t(intl, 'usage.col.entity', 'Entidade')}</TableCell><TableCell>Status</TableCell><TableCell align="right">{t(intl, 'usage.col.rows', 'Linhas')}</TableCell><TableCell>{t(intl, 'usage.col.duration', 'Duração')}</TableCell><TableCell>IP</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {children.map((r) => (
                    <TableRow key={r.id} hover style={{ cursor: 'pointer' }} onClick={() => onRowClick && onRowClick(r)}>
                      <TableCell style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtTime(r.occurredAt, true)}</TableCell>
                      <TableCell><UsageKindChip intl={intl} kind={r.actionKind} details={r.details} /></TableCell>
                      <TableCell><UsageApiChip api={r.api} /></TableCell>
                      <TableCell><Tooltip title={r.url || r.path || ''}><span>{r.route}</span></Tooltip></TableCell>
                      <TableCell>{r.entityType}{r.entityId ? <b> #{r.entityId}</b> : ''}</TableCell>
                      <TableCell><UsageStatusChip code={r.statusCode} /></TableCell>
                      <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums', color: Number(r.resultCount) >= volumeAlertRows ? '#c62828' : '#666' }}>{r.resultCount === null || r.resultCount === undefined ? '' : fmtInt(r.resultCount)}</TableCell>
                      <TableCell><UsageDurationBar ms={r.durationMs} max={maxMs} /></TableCell>
                      <TableCell style={{ whiteSpace: 'nowrap' }}>{r.ip}{r.details && r.details.forwarded ? <span style={{ color: '#c62828' }}> ⚠</span> : null}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  }
}
